import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FolderOpen, LogOut, Clock, CheckCircle, AlertTriangle, Send,
  MapPin, Euro, Ruler, Play, FileText, ChevronDown, ChevronUp, MessageSquare
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import axios from 'axios';
import ProjectCommunication from '../components/ProjectCommunication';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_CONFIG = {
  analyst_review: { label: 'À analyser', color: 'bg-blue-100 text-blue-700' },
  pending_validation: { label: 'En attente validation', color: 'bg-purple-100 text-purple-700' }
};

export default function AnalystDashboard() {
  const navigate = useNavigate();
  const [analyst, setAnalyst] = useState(null);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedProject, setSelectedProject] = useState(null);
  const [analysisForm, setAnalysisForm] = useState({ score: '', notes: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('analyst_token');
    const analystData = localStorage.getItem('analyst_data');
    
    if (!token || !analystData) {
      navigate('/analyst');
      return;
    }
    
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    setAnalyst(JSON.parse(analystData));
    fetchProjects();
  }, [navigate]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/analyst/projects`);
      setProjects(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Session expirée');
        handleLogout();
      } else {
        toast.error('Erreur lors du chargement');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('analyst_token');
    localStorage.removeItem('analyst_data');
    delete axios.defaults.headers.common['Authorization'];
    navigate('/analyst');
  };

  const submitAnalysis = async (projectId) => {
    if (!analysisForm.score) {
      toast.error('Sélectionnez un score');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/analyst/projects/${projectId}/submit?score=${analysisForm.score}&notes=${encodeURIComponent(analysisForm.notes || '')}`);
      toast.success('Analyse soumise pour validation');
      setSelectedProject(null);
      setAnalysisForm({ score: '', notes: '' });
      fetchProjects();
    } catch (error) {
      toast.error('Erreur lors de la soumission');
    } finally {
      setSubmitting(false);
    }
  };

  const formatPrice = (price) => {
    if (!price) return '0 €';
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(price);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      {/* Header */}
      <header className="bg-slate-800/50 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-6xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-white font-heading text-xl font-bold">Espace Analyste</h1>
              <p className="text-white/60 text-sm">Bonjour, {analyst?.name}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-white/10 border-white/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-blue-500/20">
                <FolderOpen className="w-6 h-6 text-blue-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">Dossiers assignés</p>
                <p className="text-2xl font-bold text-white">{projects.length}</p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 border-white/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-amber-500/20">
                <Clock className="w-6 h-6 text-amber-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">À analyser</p>
                <p className="text-2xl font-bold text-white">
                  {projects.filter(p => p.status === 'analyst_review').length}
                </p>
              </div>
            </CardContent>
          </Card>
          
          <Card className="bg-white/10 border-white/20">
            <CardContent className="p-4 flex items-center gap-4">
              <div className="p-3 rounded-xl bg-purple-500/20">
                <AlertTriangle className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <p className="text-white/60 text-sm">En attente validation</p>
                <p className="text-2xl font-bold text-white">
                  {projects.filter(p => p.status === 'pending_validation').length}
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Projects List */}
        <Card className="bg-white/10 border-white/20">
          <CardHeader>
            <CardTitle className="text-white">Mes dossiers</CardTitle>
          </CardHeader>
          <CardContent>
            {projects.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="w-12 h-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/60">Aucun dossier assigné pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {projects.map(project => {
                  const status = STATUS_CONFIG[project.status] || { label: project.status, color: 'bg-gray-100 text-gray-700' };
                  const isExpanded = selectedProject === project.id;
                  
                  return (
                    <div key={project.id} className="border border-white/10 rounded-lg overflow-hidden">
                      {/* Project Header */}
                      <div 
                        className="p-4 bg-white/5 cursor-pointer hover:bg-white/10 transition-colors"
                        onClick={() => setSelectedProject(isExpanded ? null : project.id)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <MapPin className="w-4 h-4 text-white/40" />
                              <h3 className="font-medium text-white">{project.address}</h3>
                            </div>
                            <div className="flex items-center gap-4 text-sm text-white/60">
                              <span className="flex items-center gap-1">
                                <Euro className="w-4 h-4" />
                                {formatPrice(project.purchase_price)}
                              </span>
                              <span className="flex items-center gap-1">
                                <Ruler className="w-4 h-4" />
                                {project.surface} m²
                              </span>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <Badge className={status.color}>{status.label}</Badge>
                            {isExpanded ? <ChevronUp className="w-5 h-5 text-white/40" /> : <ChevronDown className="w-5 h-5 text-white/40" />}
                          </div>
                        </div>
                      </div>

                      {/* Expanded Content */}
                      {isExpanded && (
                        <div className="p-4 border-t border-white/10 space-y-4">
                          {/* Project Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-white/40 text-xs mb-1">Prix d'achat</p>
                              <p className="text-white font-medium">{formatPrice(project.purchase_price)}</p>
                            </div>
                            <div>
                              <p className="text-white/40 text-xs mb-1">Prix revente estimé</p>
                              <p className="text-white font-medium">{formatPrice(project.estimated_resale_price)}</p>
                            </div>
                            <div>
                              <p className="text-white/40 text-xs mb-1">Surface</p>
                              <p className="text-white font-medium">{project.surface} m²</p>
                            </div>
                            <div>
                              <p className="text-white/40 text-xs mb-1">Type de vente</p>
                              <p className="text-white font-medium">{project.sale_type}</p>
                            </div>
                          </div>

                          {/* Videos */}
                          {project.videos?.length > 0 && (
                            <div>
                              <p className="text-white/60 text-sm mb-2 flex items-center gap-2">
                                <Play className="w-4 h-4" /> Vidéos ({project.videos.length})
                              </p>
                              <div className="flex gap-2 flex-wrap">
                                {project.videos.map((video, idx) => (
                                  <a
                                    key={idx}
                                    href={video.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1 bg-blue-500/20 text-blue-400 text-sm rounded hover:bg-blue-500/30 transition-colors"
                                  >
                                    Vidéo {idx + 1}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Documents */}
                          {project.documents?.length > 0 && (
                            <div>
                              <p className="text-white/60 text-sm mb-2 flex items-center gap-2">
                                <FileText className="w-4 h-4" /> Documents ({project.documents.length})
                              </p>
                              <div className="flex gap-2 flex-wrap">
                                {project.documents.map((doc, idx) => (
                                  <a
                                    key={idx}
                                    href={doc.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="px-3 py-1 bg-emerald-500/20 text-emerald-400 text-sm rounded hover:bg-emerald-500/30 transition-colors"
                                  >
                                    {doc.name || `Document ${idx + 1}`}
                                  </a>
                                ))}
                              </div>
                            </div>
                          )}

                          {/* Communication & Documents complémentaires (per dossier) */}
                          <div className="border-t border-white/10 pt-4 mt-4">
                            <h4 className="text-white font-medium mb-3 flex items-center gap-2">
                              <MessageSquare className="w-4 h-4 text-gold" />
                              Communication & Documents (avec client)
                            </h4>
                            <ProjectCommunication projectId={project.id} token={localStorage.getItem('analyst_token')} compact />
                          </div>

                          {/* Analysis Form - Only show if not yet submitted */}
                          {project.status === 'analyst_review' && (
                            <div className="border-t border-white/10 pt-4 mt-4">
                              <h4 className="text-white font-medium mb-4">Mon analyse</h4>
                              
                              <div className="space-y-4">
                                <div>
                                  <Label className="text-white/60">Score *</Label>
                                  <div className="flex gap-3 mt-2">
                                    <button
                                      type="button"
                                      onClick={() => setAnalysisForm({...analysisForm, score: 'green'})}
                                      className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                                        analysisForm.score === 'green' 
                                          ? 'border-emerald-500 bg-emerald-500/20' 
                                          : 'border-white/20 hover:border-emerald-500/50'
                                      }`}
                                    >
                                      <CheckCircle className={`w-6 h-6 mx-auto mb-1 ${analysisForm.score === 'green' ? 'text-emerald-400' : 'text-white/40'}`} />
                                      <p className={`text-sm ${analysisForm.score === 'green' ? 'text-emerald-400' : 'text-white/60'}`}>Validé</p>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setAnalysisForm({...analysisForm, score: 'orange'})}
                                      className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                                        analysisForm.score === 'orange' 
                                          ? 'border-amber-500 bg-amber-500/20' 
                                          : 'border-white/20 hover:border-amber-500/50'
                                      }`}
                                    >
                                      <AlertTriangle className={`w-6 h-6 mx-auto mb-1 ${analysisForm.score === 'orange' ? 'text-amber-400' : 'text-white/40'}`} />
                                      <p className={`text-sm ${analysisForm.score === 'orange' ? 'text-amber-400' : 'text-white/60'}`}>Risqué</p>
                                    </button>
                                    <button
                                      type="button"
                                      onClick={() => setAnalysisForm({...analysisForm, score: 'red'})}
                                      className={`flex-1 p-3 rounded-lg border-2 transition-colors ${
                                        analysisForm.score === 'red' 
                                          ? 'border-red-500 bg-red-500/20' 
                                          : 'border-white/20 hover:border-red-500/50'
                                      }`}
                                    >
                                      <AlertTriangle className={`w-6 h-6 mx-auto mb-1 ${analysisForm.score === 'red' ? 'text-red-400' : 'text-white/40'}`} />
                                      <p className={`text-sm ${analysisForm.score === 'red' ? 'text-red-400' : 'text-white/60'}`}>Refusé</p>
                                    </button>
                                  </div>
                                </div>

                                <div>
                                  <Label className="text-white/60">Notes / Commentaires</Label>
                                  <textarea
                                    value={analysisForm.notes}
                                    onChange={(e) => setAnalysisForm({...analysisForm, notes: e.target.value})}
                                    placeholder="Vos observations et recommandations..."
                                    className="w-full mt-2 p-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder:text-white/40 h-24 resize-none"
                                  />
                                </div>

                                <Button
                                  onClick={() => submitAnalysis(project.id)}
                                  disabled={submitting || !analysisForm.score}
                                  className="w-full bg-blue-500 hover:bg-blue-600 text-white"
                                >
                                  <Send className="w-4 h-4 mr-2" />
                                  {submitting ? 'Envoi...' : 'Soumettre pour validation'}
                                </Button>
                              </div>
                            </div>
                          )}

                          {/* Already submitted */}
                          {project.status === 'pending_validation' && (
                            <div className="border-t border-white/10 pt-4 mt-4 text-center">
                              <div className="inline-flex items-center gap-2 px-4 py-2 bg-purple-500/20 rounded-lg">
                                <Clock className="w-5 h-5 text-purple-400" />
                                <span className="text-purple-300">En attente de validation par l'expert</span>
                              </div>
                              {project.analyst_score && (
                                <p className="text-white/60 text-sm mt-2">
                                  Votre score : <span className={`font-medium ${
                                    project.analyst_score === 'green' ? 'text-emerald-400' :
                                    project.analyst_score === 'orange' ? 'text-amber-400' : 'text-red-400'
                                  }`}>{project.analyst_score === 'green' ? 'Validé' : project.analyst_score === 'orange' ? 'Risqué' : 'Refusé'}</span>
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
